import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CommandBar } from '../components/CommandBar';
import { ContextMenu } from '../components/ContextMenu';
import { ListFilters } from '../components/ListFilters';
import { MoveScopeDialog } from '../components/MoveScopeDialog';
import { NewTaskModal } from '../components/NewTaskModal';
import { TaskDetail } from '../components/TaskDetail';
import { TaskList } from '../components/TaskList';
import { ViewHeader } from '../components/ViewHeader';
import { ViewerToggle } from '../components/ViewerToggle';
import { useUndo } from '../context/UndoContext';
import { useAuth } from '../context/AuthContext';
import { usePersistedState } from '../hooks/usePersistedState';
import { useTaskContextMenu } from '../hooks/useTaskContextMenu';
import { useTaskKeyboard } from '../hooks/useTaskKeyboard';
import { useTaskPolling } from '../hooks/useTaskPolling';
import { searchTasks } from '../utils/searchTasks';
import type { Scope } from '../scopes';
import { labelForScope, scopeFromSlug } from '../scopes';
import type { Task, TaskPriority } from '../types';
import { sortTasks, type SortMode } from '../utils/sort';
import * as api from '../utils/apiClient';
import { NotFoundView } from './NotFoundView';

export function ScopeView() {
  const { scopeSlug } = useParams<{ scopeSlug: string }>();
  const scope = scopeFromSlug(scopeSlug);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedId = searchParams.get('t');

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showCompleted, setShowCompleted] = usePersistedState('filter.showCompleted', false);
  const [query, setQuery] = useState('');
  const [showNewTask, setShowNewTask] = useState(false);
  const [transitioningIds, setTransitioningIds] = useState<Set<string>>(new Set());
  const { user } = useAuth();
  const [viewer, setViewer] = usePersistedState<string>('viewer.selected', 'all');
  const [sortMode, setSortMode] = usePersistedState<SortMode>(
    `sort.scope.${scopeSlug ?? 'default'}`,
    'smart'
  );
  const undo = useUndo();
  const ctx = useTaskContextMenu();

  const refresh = useCallback(
    async (showSpinner = false) => {
      if (!scope) return;
      if (showSpinner) {
        setLoading(true);
        setError(null);
      }
      try {
        const ts = await api.listTasks(scope);
        setTasks(ts);
        if (showSpinner) setError(null);
      } catch (err) {
        if (showSpinner) setError((err as Error).message);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [scope]
  );

  useEffect(() => {
    void refresh(true);
  }, [refresh]);

  useTaskPolling(() => {
    void refresh();
  });

  const viewerFiltered = useMemo(() => {
    if (viewer === 'all') return tasks;
    return tasks.filter((t) => t.assigneeId === viewer);
  }, [tasks, viewer]);
  const activeCount = useMemo(() => viewerFiltered.filter((t) => t.status !== 'completed').length, [viewerFiltered]);
  const completedCount = useMemo(() => viewerFiltered.filter((t) => t.status === 'completed').length, [viewerFiltered]);
  const withCompletion = useMemo(
    () =>
      showCompleted
        ? viewerFiltered
        : viewerFiltered.filter((t) => t.status !== 'completed' || transitioningIds.has(t.taskId)),
    [viewerFiltered, showCompleted, transitioningIds]
  );
  const filtered = useMemo(() => searchTasks(withCompletion, query), [withCompletion, query]);
  const sorted = useMemo(() => sortTasks(filtered, sortMode), [filtered, sortMode]);
  const selected = sorted.find((t) => t.taskId === selectedId) ?? null;
  const selectedIdx = selected ? sorted.findIndex((t) => t.taskId === selected.taskId) : -1;

  const select = useCallback(
    (taskId: string | null) => {
      const wasSelected = searchParams.has('t');
      const willSelect = !!taskId;
      if (wasSelected && !willSelect) {
        navigate(-1);
        return;
      }
      const next = new URLSearchParams(searchParams);
      if (taskId) next.set('t', taskId);
      else next.delete('t');
      const replace = wasSelected && willSelect;
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams, navigate]
  );

  const upsertTask = useCallback((task: Task) => {
    setTasks((prev) => {
      const idx = prev.findIndex((t) => t.taskId === task.taskId);
      if (idx < 0) return [...prev, task];
      const next = [...prev];
      next[idx] = task;
      return next;
    });
  }, []);

  const removeLocalTask = useCallback((taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.taskId !== taskId));
  }, []);

  const onUpdate = useCallback(
    async (patch: Parameters<typeof api.updateTask>[2]) => {
      if (!scope || !selected) return;
      const updated = await api.updateTask(scope, selected.taskId, patch);
      upsertTask(updated);
    },
    [scope, selected, upsertTask]
  );

  const onMove = useCallback(
    async (toScope: Scope) => {
      if (!scope || !selected) return;
      await api.moveTask(scope, selected.taskId, toScope);
      removeLocalTask(selected.taskId);
      select(null);
    },
    [scope, selected, removeLocalTask, select]
  );

  const deleteWithUndo = useCallback(
    (task: Task) => {
      removeLocalTask(task.taskId);
      if (selected?.taskId === task.taskId) select(null);
      undo.queue({
        message: `Deleted: ${task.title}`,
        reverse: () => {
          upsertTask(task);
        },
        onExpire: async () => {
          try {
            await api.deleteTask(task.scope, task.taskId);
          } catch {
            // best-effort; refresh will reconcile
          }
        },
      });
    },
    [removeLocalTask, selected, select, undo, upsertTask]
  );

  const onDelete = useCallback(() => {
    if (!scope || !selected) return;
    deleteWithUndo(selected);
  }, [scope, selected, deleteWithUndo]);

  const onAddNote = useCallback(
    async (body: string) => {
      if (!scope || !selected) return;
      const updated = await api.addNote(scope, selected.taskId, body);
      upsertTask(updated);
    },
    [scope, selected, upsertTask]
  );

  const completeTaskWithUndo = useCallback(
    (original: Task) => {
      if (original.status === 'completed') return;
      upsertTask({ ...original, status: 'completed', completedAt: new Date().toISOString() });
      setTransitioningIds((prev) => new Set(prev).add(original.taskId));
      setTimeout(() => {
        setTransitioningIds((prev) => {
          const next = new Set(prev);
          next.delete(original.taskId);
          return next;
        });
      }, 600);
      undo.queue({
        message: `Completed: ${original.title}`,
        reverse: async () => {
          upsertTask(original);
          await api.updateTask(original.scope, original.taskId, { status: original.status });
        },
      });
      void api.updateTask(original.scope, original.taskId, { status: 'completed' }).catch(() => {});
    },
    [upsertTask, undo]
  );

  const completeWithUndo = useCallback(() => {
    if (!scope || !selected) return;
    completeTaskWithUndo(selected);
  }, [scope, selected, completeTaskWithUndo]);

  const createTask = useCallback(
    async (input: {
      scope: Scope;
      title: string;
      priority?: TaskPriority;
      dueDate?: string;
      note?: string;
      assigneeId?: string;
      private?: boolean;
    }) => {
      const { note, ...taskInput } = input;
      const assigneeId = taskInput.assigneeId ?? user?.userId;
      const created = await api.createTask({ ...taskInput, assigneeId });
      const final = note ? await api.addNote(created.scope, created.taskId, note) : created;
      if (input.scope === scope) {
        upsertTask(final);
        select(final.taskId);
      }
    },
    [scope, upsertTask, select, user]
  );

  const snooze = useCallback(async () => {
    if (!scope || !selected) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const updated = await api.updateTask(scope, selected.taskId, {
      dueDate: tomorrow.toISOString(),
    });
    upsertTask(updated);
  }, [scope, selected, upsertTask]);

  useTaskKeyboard(
    {
      next: () => {
        if (sorted.length === 0) return;
        const nextIdx = Math.min(sorted.length - 1, selectedIdx < 0 ? 0 : selectedIdx + 1);
        select(sorted[nextIdx].taskId);
      },
      prev: () => {
        if (sorted.length === 0) return;
        const prevIdx = Math.max(0, selectedIdx < 0 ? 0 : selectedIdx - 1);
        select(sorted[prevIdx].taskId);
      },
      close: () => select(null),
      complete: completeWithUndo,
      togglePin: () => void onUpdate({ pinned: !selected?.pinned }),
      snooze: () => void snooze(),
      addNote: () => {
        const textarea = document.querySelector<HTMLTextAreaElement>('.note-composer textarea');
        textarea?.focus();
      },
      setPriority: (priority: TaskPriority) => void onUpdate({ priority }),
      move: (toScope: Scope) => void onMove(toScope),
      toggleHelp: () => setShowHelp((v) => !v),
    },
    !showHelp
  );

  if (!scope) return <NotFoundView />;

  return (
    <div className="scope-view">
      <ViewHeader
        title={labelForScope(scope)}
        searchValue={query}
        onSearchChange={setQuery}
        onNewTask={() => setShowNewTask(true)}
        onShowShortcuts={() => setShowHelp(true)}
        leadingSlot={<ViewerToggle value={viewer} onChange={setViewer} />}
      />

      <div className={`pane-wrap ${selected ? 'has-selection' : ''}`}>
        <section className="list-pane" aria-label="Tasks">
          {loading ? (
            <div className="muted">Loading…</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <>
              <ListFilters
                showCompleted={showCompleted}
                onToggleCompleted={() => setShowCompleted((v) => !v)}
                activeCount={activeCount}
                completedCount={completedCount}
                sortMode={sortMode}
                onSortChange={setSortMode}
                viewer={viewer}
                onViewerChange={setViewer}
              />
              <TaskList
                tasks={sorted}
                selectedTaskId={selected?.taskId ?? null}
                onSelect={select}
                onContextMenu={ctx.openContextMenu}
                onSwipeComplete={completeTaskWithUndo}
                onSwipeDelete={deleteWithUndo}
                emptyMessage={
                  query ? 'No tasks match your search.' : `No tasks in ${labelForScope(scope)}.`
                }
              />
            </>
          )}
        </section>

        <section className="detail-pane" aria-label="Task detail">
          {selected ? (
            <TaskDetail
              task={selected}
              onUpdate={onUpdate}
              onMove={onMove}
              onDelete={onDelete}
              onAddNote={onAddNote}
              onBack={() => select(null)}
            />
          ) : (
            <div className="muted centered">Select a task (or press j/k).</div>
          )}
        </section>
      </div>

      <CommandBar open={showHelp} onClose={() => setShowHelp(false)} />

      <NewTaskModal
        open={showNewTask}
        defaultScope={scope}
        onClose={() => setShowNewTask(false)}
        onCreate={createTask}
      />

      {ctx.contextMenu && (
        <ContextMenu
          x={ctx.contextMenu.x}
          y={ctx.contextMenu.y}
          items={ctx.buildMenuItems(ctx.contextMenu.task, upsertTask, removeLocalTask, deleteWithUndo)}
          onClose={ctx.closeContextMenu}
        />
      )}

      <MoveScopeDialog
        open={!!ctx.moveDialogFor}
        currentScope={ctx.moveDialogFor?.scope ?? null}
        onSelect={(toScope) => {
          if (ctx.moveDialogFor) {
            void ctx.onMoveScope(ctx.moveDialogFor, toScope, upsertTask, removeLocalTask);
          }
        }}
        onClose={ctx.closeMoveDialog}
      />
    </div>
  );
}
